if (!customElements.get("byte-drag-scroll")) {
  class ByteDragScroll extends HTMLElement {
    constructor() {
      super();

      this.isPointerDown = false;
      this.isDragging = false;
      this.shouldSuppressClick = false;
      this.activePointerId = null;
      this.dragStartX = 0;
      this.dragStartScrollLeft = 0;
      this.dragThreshold = 5;

      this.boundHandlePointerDown = this.handlePointerDown.bind(this);
      this.boundHandlePointerMove = this.handlePointerMove.bind(this);
      this.boundHandlePointerUp = this.handlePointerUp.bind(this);
      this.boundHandleClickCapture = this.handleClickCapture.bind(this);
      this.boundHandleDragStart = this.handleDragStart.bind(this);
    }

    connectedCallback() {
      this.#cacheElements();
      if (!this.viewport) return;

      this.#bindEvents();
    }

    disconnectedCallback() {
      this.#unbindEvents();
    }

    handlePointerDown(event) {
      // Touch/pen already scroll natively; only handle mouse.
      if (event.pointerType !== "mouse") return;
      if (event.button !== 0) return;

      const maxScrollLeft =
        this.viewport.scrollWidth - this.viewport.clientWidth;
      if (maxScrollLeft <= 0) return;

      this.isPointerDown = true;
      this.isDragging = false;
      this.activePointerId = event.pointerId;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = this.viewport.scrollLeft;
    }

    handlePointerMove(event) {
      if (!this.isPointerDown) return;
      if (event.pointerId !== this.activePointerId) return;

      const deltaX = event.clientX - this.dragStartX;

      if (!this.isDragging) {
        if (Math.abs(deltaX) < this.dragThreshold) return;
        this.#startDragging();
      }

      this.viewport.scrollLeft = this.dragStartScrollLeft - deltaX;
    }

    handlePointerUp(event) {
      if (!this.isPointerDown) return;
      if (event.pointerId !== this.activePointerId) return;

      this.#stopDragging();
    }

    handleClickCapture(event) {
      if (!this.shouldSuppressClick) return;

      // Prevent product card links from firing after a drag.
      event.preventDefault();
      event.stopPropagation();
      this.shouldSuppressClick = false;
    }

    handleDragStart(event) {
      // Block native image/link ghost dragging.
      event.preventDefault();
    }

    #cacheElements() {
      this.viewport = this.querySelector("[data-fc-viewport]");
    }

    #startDragging() {
      this.isDragging = true;
      this.viewport.setPointerCapture(this.activePointerId);
      this.viewport.style.scrollSnapType = "none";
      this.viewport.style.scrollBehavior = "auto";
      this.viewport.classList.add("is-dragging");
    }

    #stopDragging() {
      const wasDragging = this.isDragging;

      if (wasDragging) {
        if (this.viewport.hasPointerCapture(this.activePointerId)) {
          this.viewport.releasePointerCapture(this.activePointerId);
        }

        this.viewport.style.scrollSnapType = "";
        this.viewport.style.scrollBehavior = "";
        this.viewport.classList.remove("is-dragging");

        this.shouldSuppressClick = true;
        setTimeout(() => {
          this.shouldSuppressClick = false;
        }, 0);
      }

      this.isPointerDown = false;
      this.isDragging = false;
      this.activePointerId = null;
    }

    #bindEvents() {
      this.viewport.addEventListener(
        "pointerdown",
        this.boundHandlePointerDown,
      );
      this.viewport.addEventListener(
        "pointermove",
        this.boundHandlePointerMove,
      );
      this.viewport.addEventListener("pointerup", this.boundHandlePointerUp);
      this.viewport.addEventListener(
        "pointercancel",
        this.boundHandlePointerUp,
      );
      this.viewport.addEventListener(
        "click",
        this.boundHandleClickCapture,
        true,
      );
      this.viewport.addEventListener("dragstart", this.boundHandleDragStart);
    }

    #unbindEvents() {
      if (!this.viewport) return;

      this.viewport.removeEventListener(
        "pointerdown",
        this.boundHandlePointerDown,
      );
      this.viewport.removeEventListener(
        "pointermove",
        this.boundHandlePointerMove,
      );
      this.viewport.removeEventListener("pointerup", this.boundHandlePointerUp);
      this.viewport.removeEventListener(
        "pointercancel",
        this.boundHandlePointerUp,
      );
      this.viewport.removeEventListener(
        "click",
        this.boundHandleClickCapture,
        true,
      );
      this.viewport.removeEventListener("dragstart", this.boundHandleDragStart);
    }
  }

  customElements.define("byte-drag-scroll", ByteDragScroll);
}
